import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  ShoppingCart,
  RefreshCw,
  Trash2,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const STATI = [
  "In raccolta",
  "Confermato",
  "Annullato",
  "Completato",
];

export default function AdesioniAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/adesioni-gruppo`, { headers });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento adesioni");
      }
      const json = await res.json();
      setItems(json.data || []);
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const updateStato = async (id, stato_adesione) => {
    setUpdatingId(id);
    try {
      const res = await fetch(`${API}/api/admin/adesioni-gruppo/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ stato_adesione }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Aggiornamento fallito");
      toast.success("Stato aggiornato");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa adesione?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/adesioni-gruppo/${id}`, {
        method: "DELETE",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Eliminazione fallita");
      toast.success("Adesione eliminata");
      await load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-orange-600" />
            Adesioni gruppi
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Adesioni ai gruppi di acquisto (Risparmio).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((a) => {
          const stato = a.stato_adesione || a.stato || "—";
          return (
            <Card key={a.id}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">
                    {a.name || a.nome_prodotto || a.sku_prodotto || "Adesione"}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {[
                      a.struttura_nome || a.struttura_id,
                      a.sku_prodotto,
                      a.id_gruppo_acquisto
                        ? `Gruppo ${a.id_gruppo_acquisto}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="secondary">{stato}</Badge>
                    {a.quantita_mensile_stimata != null && (
                      <span className="text-xs text-slate-400">
                        Qty: {a.quantita_mensile_stimata}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={stato}
                    onValueChange={(v) => updateStato(a.id, v)}
                    disabled={updatingId === a.id}
                  >
                    <SelectTrigger className="w-40 h-9">
                      <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set([stato, ...STATI].filter((s) => s && s !== "—"))].map(
                        (s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  {updatingId === a.id && (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    disabled={deletingId === a.id}
                    onClick={() => handleDelete(a.id)}
                  >
                    {deletingId === a.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!loading && items.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-slate-500">
              Nessuna adesione
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
